import type { TaskInstanceState, TaskInstanceStatus } from "~/services/dagRuns";

// One row in the task list: a normal task's single instance, or a
// dynamically-mapped task's (Airflow's `.expand()`) collapsed instances —
// several of which share one `taskId`, distinguished only by `mapIndex`.
export interface TaskGroupViewModel {
  taskId: string;
  isMapped: boolean;
  // Sorted by mapIndex ascending; the single [-1] entry for a normal task.
  instances: TaskInstanceStatus[];
  // A Map (not a plain object) so its keys keep their literal type through
  // iteration — `Object.entries` on a `Record<K, V>` always erases `K` to
  // `string`, which would otherwise force an `as` cast at every call site.
  stateCounts: ReadonlyMap<TaskInstanceState | "none", number>;
}

const stateKey = (state: TaskInstanceState | null): TaskInstanceState | "none" => state ?? "none";

// Preserves first-seen order — matches the backend's DAG-topological order
// (Airflow's own Grid view). Don't re-sort by start time: a skip-cascaded
// downstream branch can finish near-instantly, giving it an earlier
// timestamp than an upstream task still queued behind a busy worker.
//
// A group is "mapped" if it has multiple instances, or its lone instance
// has mapIndex >= 0 (a mapped task that produced exactly one instance).
export function groupTaskInstances(tasks: TaskInstanceStatus[]): TaskGroupViewModel[] {
  const order: string[] = [];
  const byTaskId = new Map<string, TaskInstanceStatus[]>();

  for (const task of tasks) {
    const existing = byTaskId.get(task.taskId);
    if (existing) {
      existing.push(task);
    } else {
      byTaskId.set(task.taskId, [task]);
      order.push(task.taskId);
    }
  }

  return order.map((taskId) => {
    const instances = [...byTaskId.get(taskId)!].sort((a, b) => a.mapIndex - b.mapIndex);
    const isMapped = instances.length > 1 || instances[0].mapIndex >= 0;

    const stateCounts = new Map<TaskInstanceState | "none", number>();
    for (const instance of instances) {
      const key = stateKey(instance.state);
      stateCounts.set(key, (stateCounts.get(key) ?? 0) + 1);
    }

    return { taskId, isMapped, instances, stateCounts };
  });
}

// Preference order for which non-terminal instance "speaks for" a mapped
// group — an actually-running instance beats a merely-retrying one.
const NON_TERMINAL_BUCKET_PRIORITY: TaskStateBucket[] = ["running", "retrying"];

// Single icon/label for the group. Unmapped: the one instance's state.
// Mapped: worst-status-wins — failed dominates, else the most informative
// still-in-flight instance, else null if only unscheduled remain, else success.
//
// Never synthesizes `"running"` — that would contradict the count-breakdown
// text for the same group (e.g. an outstanding `up_for_retry` instance).
export function effectiveGroupState(group: TaskGroupViewModel): TaskInstanceState | null {
  if (!group.isMapped) {
    return group.instances[0].state;
  }
  const states = group.instances.map((i) => i.state);
  if (states.some((s) => taskStateBucket(s ?? "none") === "failed")) {
    return "failed";
  }
  for (const bucket of NON_TERMINAL_BUCKET_PRIORITY) {
    const match = states.find((s) => s !== null && taskStateBucket(s) === bucket);
    if (match) {
      return match;
    }
  }
  if (states.some((s) => s === null)) {
    // Not yet scheduled — `null` correctly buckets to "pending" via
    // `taskStateBucket`, matching what the count breakdown already shows.
    return null;
  }
  return "success";
}

// A raw Airflow task-instance state collapsed to one of a small set of visual
// categories — the single source of truth both the icon map and the task
// list's count-breakdown label read from, so they can't drift out of sync.
export type TaskStateBucket = "success" | "running" | "failed" | "retrying" | "pending";

export function taskStateBucket(state: TaskInstanceState | "none" | null): TaskStateBucket {
  switch (state) {
    case "success":
      return "success";
    case "running":
    case "queued":
    case "scheduled":
    case "deferred":
      return "running";
    case "failed":
    case "upstream_failed":
      return "failed";
    case "up_for_retry":
    case "up_for_reschedule":
    case "restarting":
      return "retrying";
    case "skipped":
    case "removed":
    case "none":
    case null:
    default:
      return "pending";
  }
}
